// Prep-phase banner — surfaces the rest actions and Ready button for the
// current user during the lobby. The gathering of the party lives in the
// drawer (#381 split); this banner gets the centered main real estate.

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';
import './rest-action.js';

/**
 * @typedef {import('../../types/jsdoc-types.js').Character} Character
 * @typedef {import('../../types/jsdoc-types.js').RestAction} RestAction
 * @typedef {import('../../state/map-state.js').WaitingRoom} WaitingRoom
 */

class PrepPhaseBanner extends BaseComponent {
  static properties = {
    show: { type: Boolean },
    waitingRoom: { type: Object },
    playerCharacters: { type: Array },
    currentUserId: { type: String, attribute: 'current-user-id' },
  };

  static styles = css`
    :host {
      display: block;
    }

    .prep-banner {
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding: 16px 32px;
      background: rgba(0, 0, 0, 0.85);
      color: white;
      border-radius: 12px;
      border: 1px solid #5a4a2a;
      backdrop-filter: blur(4px);
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
      width: min(960px, 92vw);
    }

    .header {
      font-size: 28px;
      font-weight: 700;
      color: #e8c56a;
      text-align: center;
    }

    .subtext {
      font-size: 16px;
      color: #cfcfcf;
      line-height: 1.5;
      text-align: center;
    }

    .subtext p {
      margin: 0 0 10px 0;
    }

    .subtext p:last-child {
      margin-bottom: 0;
    }

    .empty {
      font-size: 13px;
      color: #aaa;
      font-style: italic;
      text-align: center;
    }

    /* Two-column layout for rest action cards. Mirrors .actions-grid in
       game-map.js (same gap, equal-width columns) but capped at two
       columns so cards stay readable inside the banner. align-items:start
       lets each card size to its own content rather than stretching to
       match the tallest, so summaries top-align cleanly across cards. */
    .rest-actions-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 1rem;
      align-items: start;
    }

    .actions {
      display: flex;
      justify-content: center;
      margin-top: 4px;
    }

    .ready-btn {
      background: #4caf50;
      border: none;
      color: white;
      padding: 8px 28px;
      border-radius: 6px;
      font-size: 15px;
      font-weight: 600;
      cursor: pointer;
      transition: background 0.2s;
    }

    .ready-btn:hover:not(:disabled) {
      background: #43a047;
    }

    .ready-btn:disabled {
      background: #b9b9b9;
      cursor: not-allowed;
    }

    .ready-status {
      color: #4caf50;
      font-weight: 600;
      font-size: 14px;
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }
  `;

  constructor() {
    super();
    this.show = false;
    /** @type {WaitingRoom|null} */
    this.waitingRoom = null;
    /** @type {Character[]} */
    this.playerCharacters = [];
    this.currentUserId = '';
  }

  /** @returns {RestAction[]} */
  _currentUserRestActions() {
    if (!this.currentUserId) return [];
    const me = this.playerCharacters.find((c) => c.playerId === this.currentUserId || c.userId === this.currentUserId);
    if (!me) return [];
    return Array.isArray(me.restActions) ? me.restActions : [];
  }

  /**
   * Frontend-only "Change Equipment" card. Shaped like a server-emitted
   * RestAction so it renders through <rest-action>; clicking it routes to
   * the equipment manager via game-map's rest-action-click handler.
   * @param {string} characterId
   * @returns {RestAction}
   */
  _changeEquipmentAction(characterId) {
    return /** @type {RestAction} */ ({
      type: 'open:equipment-manager',
      characterId,
      label: 'Change Equipment',
      description: 'Open the equipment manager to change what you have equipped',
      icon: 'backpack',
      choiceType: 'no-choice',
      options: [],
      currentSelections: [],
      count: 0,
      enabled: true,
    });
  }

  _isCurrentUserReady() {
    const seats = this.waitingRoom?.seats || [];
    const seat = seats.find((s) => s.playerId === this.currentUserId);
    return seat?.ready === true;
  }

  _hasSeat() {
    const seats = this.waitingRoom?.seats || [];
    return seats.some((s) => s.playerId === this.currentUserId);
  }

  _handleReadyClick() {
    this.dispatchEvent(new CustomEvent('ready-click', { bubbles: true, composed: true }));
  }

  render() {
    if (!this.show) return html``;

    const restActions = this._currentUserRestActions();
    const isReady = this._isCurrentUserReady();
    const hasSeat = this._hasSeat();
    const me = this.playerCharacters.find((c) => c.playerId === this.currentUserId || c.userId === this.currentUserId);
    const equipmentAction = me ? this._changeEquipmentAction(me.id) : null;

    return html`
      <div class="prep-banner">
        <div class="header">Prepare for the Adventure</div>
        <div class="subtext">
          <p>
            You start the adventure with the benefit of a long rest, restoring your hit points, class feature uses,
            spell slots, and other resources that reset after a long rest.
          </p>
          <p>
            Before starting the adventure, review the long-rest actions available to you below. When you are ready,
            click the button below.
          </p>
        </div>
        <div class="rest-actions-grid">
          ${equipmentAction ? html`<rest-action .action=${equipmentAction}></rest-action>` : ''}
          ${restActions.map((a) => html`<rest-action .action=${a}></rest-action>`)}
        </div>
        <div class="actions">
          ${
            isReady
              ? html`
                <span class="ready-status">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="3"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Ready — waiting for the rest of the party
                </span>
              `
              : html`
                <button class="ready-btn" ?disabled="${!hasSeat}" @click="${this._handleReadyClick}">Ready</button>
              `
          }
        </div>
      </div>
    `;
  }
}

customElements.define('prep-phase-banner', PrepPhaseBanner);
